import { HeaderCTAButton } from '@/components/buttons/HeaderCTAButton';
import { AppLink } from '@/components/links/AppLink';
import { useManifest } from '@/contexts/ManifestContext';
import DesktopNav from '@/layout/DesktopHeader/DesktopNav';
import LanguageSelector from '@/layout/LanguageSelector';
import { Routes } from '@/routes';
import { cn } from '@/utils/cn';

interface DesktopHeaderProps {
  /** Progressive nav: translate the bar out of view (hide on scroll-down). */
  hidden?: boolean;
}

const DesktopHeader: React.FC<DesktopHeaderProps> = ({ hidden = false }) => {
  const { features, header, mainSiteUrl } = useManifest();

  // When a CTA is shown, the links sit on the left next to the logo and the
  // CTA (plus language switch) live on the right. When there is no CTA, the
  // links move to the right side of the bar (white-label "without CTA" layout).
  const linksLeft = Boolean(header.cta?.display);

  return (
    <header
      className={cn(
        'bg-neutral-white border-neutral-light 3xl:px-32 sticky top-0 z-100 flex w-full min-w-full items-center justify-between border-b px-4 py-4 transition-transform duration-300 ease-in-out xl:px-24',
        hidden ? '-translate-y-full' : 'translate-y-0',
      )}
    >
      <div className="flex items-center gap-8">
        <AppLink
          href={mainSiteUrl ?? Routes.Home}
          className="text-primary-alt shrink-0"
        >
          {/* 40px tall, so the content row hugs 40px (bar = 40 + 16px×2 = 72px). */}
          <img
            src={header.logo.url}
            alt="logo"
            className="h-10 w-auto group-hover:opacity-90"
          />
        </AppLink>
        {linksLeft && <DesktopNav />}
      </div>

      <div className="text-primary flex items-center gap-6">
        {!linksLeft && <DesktopNav />}
        {features.enableI18n && <LanguageSelector variant="dropdown" />}
        <HeaderCTAButton />
      </div>
    </header>
  );
};

export default DesktopHeader;
